/**
 * AI Service - Direct OpenAI API integration without SDK
 * Uses fetch API to avoid bundling large Node.js OpenAI SDK
 */

/**
 * Request queue and throttling configuration
 */
const requestQueue = [];
let isProcessingQueue = false;
const MIN_REQUEST_INTERVAL = 2000; // 2 seconds between requests
let lastRequestTime = 0;

/**
 * Retry configuration with exponential backoff
 */
const RETRY_CONFIG = {
  maxRetries: 3,
  initialDelay: 1000, // 1 second
  maxDelay: 30000, // 30 seconds
  backoffMultiplier: 2
};

/**
 * Sleep utility for delays
 */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Calculate exponential backoff delay
 */
function getBackoffDelay(retryCount) {
  const delay = RETRY_CONFIG?.initialDelay * Math.pow(RETRY_CONFIG?.backoffMultiplier, retryCount);
  return Math.min(delay, RETRY_CONFIG?.maxDelay);
}

/**
 * Throttle requests to avoid rate limits
 */
async function throttleRequest() {
  const now = Date.now();
  const timeSinceLastRequest = now - lastRequestTime;
  
  if (timeSinceLastRequest < MIN_REQUEST_INTERVAL) {
    const waitTime = MIN_REQUEST_INTERVAL - timeSinceLastRequest;
    await sleep(waitTime);
  }
  
  lastRequestTime = Date.now();
}

/**
 * Make OpenAI API call with retry logic using fetch
 */
async function makeOpenAIRequest(messages, retryCount = 0) {
  const apiKey = import.meta.env?.VITE_OPENAI_API_KEY;
  
  if (!apiKey || apiKey === 'your-openai-api-key-here') {
    throw new Error('OpenAI API key is not configured. Please add VITE_OPENAI_API_KEY to your .env file.');
  }

  try {
    await throttleRequest();
    
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`,
      },
      body: JSON.stringify({
        model: 'gpt-4o-mini',
        messages: messages,
        temperature: 0.7,
        response_format: { type: 'json_object' },
      }),
    });

    if (!response?.ok) {
      const errorData = await response?.json()?.catch(() => ({}));
      throw new Error(errorData?.error?.message || `OpenAI API error: ${response.status}`);
    }

    return await response?.json();
  } catch (error) {
    // Handle rate limits and retryable errors
    if (retryCount < RETRY_CONFIG?.maxRetries) {
      const isRateLimitError = error?.message?.includes('429') || error?.message?.includes('rate limit');
      const isServerError = error?.message?.includes('500') || error?.message?.includes('503');
      
      if (isRateLimitError || isServerError) {
        const backoffDelay = getBackoffDelay(retryCount);
        console.warn(`OpenAI request failed (attempt ${retryCount + 1}/${RETRY_CONFIG?.maxRetries}). Retrying in ${backoffDelay}ms...`);
        await sleep(backoffDelay);
        return makeOpenAIRequest(messages, retryCount + 1);
      }
    }
    
    throw error;
  }
}

/**
 * Process request queue sequentially
 */
async function processQueue() {
  if (isProcessingQueue || requestQueue?.length === 0) {
    return;
  }
  
  isProcessingQueue = true;
  
  while (requestQueue?.length > 0) {
    const { messages, resolve, reject } = requestQueue?.shift();
    
    try {
      const result = await makeOpenAIRequest(messages);
      resolve(result);
    } catch (error) {
      reject(error);
    }
  }
  
  isProcessingQueue = false;
}

/**
 * Queue an OpenAI request
 */
function queueOpenAIRequest(messages) {
  return new Promise((resolve, reject) => {
    requestQueue?.push({ messages, resolve, reject });
    processQueue();
  });
}

/**
 * Analyze offer using OpenAI GPT-4o-mini
 */
export async function analyzeOffer(offerData, propertyData) {
  try {
    // Validate inputs
    if (!offerData || !propertyData) {
      throw new Error('Missing required data for offer analysis');
    }

    // Validate API key before attempting to use OpenAI
    const apiKey = import.meta.env?.VITE_OPENAI_API_KEY;
    if (!apiKey || apiKey === 'your-openai-api-key-here') {
      throw new Error('OpenAI API key is not configured. Please add VITE_OPENAI_API_KEY to your .env file.');
    }

    // Format offer data for analysis
    const offerSummary = `
      Offer Amount: $${offerData?.offerAmount?.toLocaleString() || 'N/A'}
      Earnest Money: $${offerData?.earnestMoney?.toLocaleString() || 'N/A'}
      Closing Date: ${offerData?.closingDate || 'N/A'}
      Contingencies: ${offerData?.contingencies || 'None specified'}
      Buyer Name: ${offerData?.buyerName || 'N/A'}
      Buyer Agent: ${offerData?.buyerAgent || 'N/A'}
    `;

    const propertySummary = `
      Address: ${propertyData?.address || 'N/A'}
      List Price: $${propertyData?.listPrice?.toLocaleString() || 'N/A'}
      Property Type: ${propertyData?.propertyType || 'N/A'}
      Bedrooms: ${propertyData?.bedrooms || 'N/A'}
      Bathrooms: ${propertyData?.bathrooms || 'N/A'}
      Square Feet: ${propertyData?.squareFeet?.toLocaleString() || 'N/A'}
    `;

    const messages = [
      {
        role: 'system',
        content: `You are a real estate expert analyzing offers. Provide analysis in JSON format with these fields:
        - strengths: array of 2-3 key strengths
        - concerns: array of 2-3 potential concerns
        - recommendation: string with overall recommendation
        - competitiveness: string ("Strong", "Moderate", or "Weak")
        - riskLevel: string ("Low", "Medium", or "High")`
      },
      {
        role: 'user',
        content: `Analyze this offer:\n\nProperty Details:${propertySummary}\n\nOffer Details:${offerSummary}`
      }
    ];

    const result = await queueOpenAIRequest(messages);

    if (!result || !result?.choices || !result?.choices?.[0] || !result?.choices?.[0]?.message || !result?.choices?.[0]?.message?.content) {
      throw new Error('Invalid response from OpenAI API');
    }

    const analysis = JSON.parse(result?.choices?.[0]?.message?.content);

    return {
      strengths: analysis?.strengths || [],
      concerns: analysis?.concerns || [],
      recommendation: analysis?.recommendation || 'Unable to generate recommendation',
      competitiveness: analysis?.competitiveness || 'Moderate',
      riskLevel: analysis?.riskLevel || 'Medium',
      analyzedAt: new Date()?.toISOString()
    };
  } catch (error) {
    console.error('Error analyzing offer with AI:', error);
    throw new Error(`Failed to analyze offer: ${error?.message}`);
  }
}

/**
 * Generate comparative analysis for multiple offers
 */
export async function compareOffers(offers, propertyData) {
  try {
    if (!offers || offers?.length === 0) {
      throw new Error('No offers provided for comparison');
    }

    // Validate API key
    const apiKey = import.meta.env?.VITE_OPENAI_API_KEY;
    if (!apiKey || apiKey === 'your-openai-api-key-here') {
      throw new Error('OpenAI API key is not configured');
    }

    const offersSummary = offers?.map((offer, index) => `
      Offer ${index + 1}:
      - Amount: $${offer?.offerAmount?.toLocaleString()}
      - Earnest Money: $${offer?.earnestMoney?.toLocaleString()}
      - Closing Date: ${offer?.closingDate}
      - Contingencies: ${offer?.contingencies || 'None'}
      - Buyer: ${offer?.buyerName}
    `)?.join('\n');

    const messages = [
      {
        role: 'system',
        content: `You are a real estate expert comparing multiple offers. Provide comparison in JSON format with:
        - topOffer: number (1-based index of best offer)
        - reasoning: string explaining why
        - keyDifferences: array of strings highlighting main differences
        - recommendations: array of strings with advice for each offer`
      },
      {
        role: 'user',
        content: `Compare these offers for a property listed at $${propertyData?.listPrice?.toLocaleString()}:\n${offersSummary}`
      }
    ];

    const result = await queueOpenAIRequest(messages);

    if (!result || !result?.choices || !result?.choices?.[0] || !result?.choices?.[0]?.message || !result?.choices?.[0]?.message?.content) {
      throw new Error('Invalid response from OpenAI API');
    }

    return JSON.parse(result?.choices?.[0]?.message?.content);
  } catch (error) {
    console.error('Error comparing offers:', error);
    throw new Error(`Failed to compare offers: ${error?.message}`);
  }
}

/**
 * Generate insights for property based on offers received
 */
export async function generatePropertyInsights(propertyData, offers) {
  try {
    if (!propertyData) {
      throw new Error('Property data is required');
    }

    // Validate API key
    const apiKey = import.meta.env?.VITE_OPENAI_API_KEY;
    if (!apiKey || apiKey === 'your-openai-api-key-here') {
      throw new Error('OpenAI API key is not configured');
    }

    const offerStats = offers && offers?.length > 0 ? `
      Total Offers: ${offers?.length}
      Average Offer: $${(offers?.reduce((sum, o) => sum + (o?.offerAmount || 0), 0) / offers?.length)?.toLocaleString()}
      Highest Offer: $${Math.max(...offers?.map(o => o?.offerAmount || 0))?.toLocaleString()}
      Lowest Offer: $${Math.min(...offers?.map(o => o?.offerAmount || 0))?.toLocaleString()}
    ` : 'No offers received yet';

    const messages = [
      {
        role: 'system',
        content: `You are a real estate market analyst. Provide insights in JSON format with:
        - marketPosition: string describing how property is positioned
        - pricingAnalysis: string analyzing pricing strategy
        - suggestions: array of 2-3 actionable suggestions
        - marketTrend: string ("Hot", "Moderate", or "Slow")`
      },
      {
        role: 'user',
        content: `Analyze this property:\nList Price: $${propertyData?.listPrice?.toLocaleString()}\nDays on Market: ${propertyData?.daysOnMarket || 'N/A'}\n\nOffer Statistics:${offerStats}`
      }
    ];

    const result = await queueOpenAIRequest(messages);

    if (!result || !result?.choices || !result?.choices?.[0] || !result?.choices?.[0]?.message || !result?.choices?.[0]?.message?.content) {
      throw new Error('Invalid response from OpenAI API');
    }

    return JSON.parse(result?.choices?.[0]?.message?.content);
  } catch (error) {
    console.error('Error generating property insights:', error);
    throw new Error(`Failed to generate insights: ${error?.message}`);
  }
}

export default {
  analyzeOffer,
  compareOffers,
  generatePropertyInsights
};
function aiService(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: aiService is not implemented yet.', args);
  return null;
}

export { aiService };
function generateOfferAnalysis(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: generateOfferAnalysis is not implemented yet.', args);
  return null;
}

export { generateOfferAnalysis };